import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LombardFacade } from '@zero/lombard/domain';
import { BlinkDirective } from '@zero/shared/ui-directives';
import { ButtonComponent, CopyToClipboardComponent, IconComponent, ModalComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { Customer, PortfolioPerformance } from '@zero/shared/util-models';
import { AuthService, DataService, RegistrationUserDataService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'home-depot-header',
  templateUrl: './depot-header.component.html',
  styleUrls: ['./depot-header.component.scss'],
  imports: [
    BlinkDirective,
    RouterLink,
    CopyToClipboardComponent,
    AsyncPipe,
    DecimalPipe,
    TranslocoPipe,
    NgClass,
    ButtonComponent,
    IconComponent,
    ModalComponent,
  ],
})
export class DepotHeaderComponent implements OnInit {
  @Input() public performance: PortfolioPerformance;
  @Input() public isPerformanceReady: boolean;

  @Output() public customerIsWaitingForOpening = new EventEmitter<void>();

  public isPotentialCustomer = false;
  public currentCustomer: Customer;
  public openingStarted = false;
  public alreadyCustomer = true;
  public commonRoutes = commonRoutes;
  public isPerformanceToday = false;

  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #registrationUserDataService = inject(RegistrationUserDataService);
  readonly #destroyRef = inject(DestroyRef);
  public readonly dataService = inject(DataService);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  public readonly lombardFacade = inject(LombardFacade);
  public readonly modal = inject(NgbModal);

  public ngOnInit(): void {
    this.customerStateFacade.loadCustomerBaseData(true);

    this.#determineTypeOfPerformance();
    this.#authService
      .isPotentialCustomer()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isPotentialCustomer) => {
        this.isPotentialCustomer = isPotentialCustomer;

        if (!this.isPotentialCustomer) {
          this.customerStateFacade.loadCustomerAccountData(true);
          return;
        }

        this.alreadyCustomer = false;
        this.#registrationUserDataService.loadUserData().then(({ status }) => {
          if (
            [
              'DATA_ENTRY_FINISHED', // SMr: to be removed as soon as status does not exist in DB anymore
              'IN_IDENTIFICATION',
              'IDENTIFICATION_ABORTED',
              'IDENTIFICATION_CANCELED',
            ].find((stat) => status === stat)
          ) {
            this.openingStarted = true;
            this.alreadyCustomer = false;
          } else if (status?.startsWith('IDENTIFICATION_')) {
            this.alreadyCustomer = true;
            this.customerIsWaitingForOpening.emit();
          }
        });
      });

    this.currentCustomer = this.dataService.getSelectedCustomerItem();

    this.dataService.selectedCustomerChanged
      .pipe(
        filter((newCust) => !!newCust),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.currentCustomer = this.dataService.getSelectedCustomerItem();
      });
  }

  public toggleTypeOfPerformance = (): void => {
    this.isPerformanceToday = !this.isPerformanceToday;
    localStorage.setItem('isPerformanceToday', JSON.stringify(this.isPerformanceToday));
  };

  public async navigateToOrderMask(): Promise<void> {
    await this.#router.navigate([this.isPotentialCustomer ? commonRoutes.prelogin.preview : commonRoutes.postlogin.buyAndSell]);
  }

  public async navigateToMultiOrderMask(): Promise<void> {
    await this.#router.navigate([this.isPotentialCustomer ? commonRoutes.prelogin.preview : commonRoutes.postlogin.multiOrder]);
  }

  public async navigateToDeposit(): Promise<void> {
    await this.#router.navigate([commonRoutes.postlogin.deposit]);
  }

  public async navigateToStatistics(): Promise<void> {
    await this.#router.navigate([commonRoutes.postlogin.stats]);
  }

  #determineTypeOfPerformance = (): void => {
    const isPerformanceToday = localStorage.getItem('isPerformanceToday');
    if (!isPerformanceToday) {
      this.isPerformanceToday = false;
      return;
    }

    this.isPerformanceToday = Boolean(JSON.parse(isPerformanceToday));
  };
}
